import { useEffect, useState, useMemo } from "react";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { safeSetTimeout, ServerTimeHelper } from "@kaltura/mediaspace-shared-utils";
import { HeroProps } from "../HeroProps";
import Lobby from "../lobby/Lobby";
import { KalturaLiveStreamScheduleEvent } from "kaltura-typescript-client/api/types/KalturaLiveStreamScheduleEvent";
import { Player } from "@kaltura/mediaspace-shared-player";
import { CommonHeroLayout } from "../layout/CommonHeroLayout";
import { getPropsToPass, useThumbnailSizing } from "../helpers";
import { MediaLobbyDataContainer } from "../lobby/lobby-data/LobbyData";
import { KmsTypeMediaSchedulingData } from "@kaltura/mediaspace-shared-types";

export interface ScheduledMediaHeroProps extends Omit<HeroProps, "schedulingData"> {
    scheduleEvent: KalturaLiveStreamScheduleEvent;

    schedulingData: KmsTypeMediaSchedulingData;
}

/**
 * wrapper component for scheduled media
 */
const ScheduledMediaHero = (props: ScheduledMediaHeroProps) => {
    const { schedulingData, scheduleEvent, entry, actions = [], thumbnailUrl, playerConfig, isReadOnly } = props;

    const media = useMemo(() => ({ entryId: entry.id, ks: playerConfig.ks as string }), [entry, playerConfig]);

    const config = useConfig();
    const serviceUrl = config?.application?.serviceUrl;

    const ONE_HOUR = 3600000; /* 60*60*1000 */
    const TWELVE_HOURS = 43200000; /* 60*60*1000*12 */
    const [showLobby, setShowLobby] = useState(true);

    // get time from server, then init accordingly
    useEffect(() => {
        /**
         * logic of what to show when (media vs lobby), expected to only run once
         * @param serverNow
         */
        const decideWhatToDo = (serverNow: number) => {
            const now = serverNow ? serverNow : Date.now();
            const prestartInMs = scheduleEvent.preStartTime ? scheduleEvent.preStartTime * 1000 : 0;
            const postEndInMs = scheduleEvent.postEndTime ? scheduleEvent.postEndTime * 1000 : 0;

            ServerTimeHelper.setTimeForDiff(now);

            // if after scheduling window - show hero section
            if (schedulingData.to && schedulingData.to + postEndInMs < now) {
                console.info(">> time is over");
                setShowLobby(true);
            }

            // if more than 12 hours before scheduling window, show hero section and do nothing.
            else if (schedulingData.from - prestartInMs - now > TWELVE_HOURS) {
                console.info(">> it is not yet time, more than 12 hours before scheduling window");
                setShowLobby(true);
            }

            // if more than 1 hour before scheduling window, show hero section and set timeout to refresh in an hour
            else if (schedulingData.from - prestartInMs - now > ONE_HOUR) {
                console.info(">> it is not yet time");
                const n = Math.floor((schedulingData.from - prestartInMs - now) / ONE_HOUR);
                console.log("will reload in " + n + " hours");
                safeSetTimeout(
                    () => {
                        window.location.reload();
                    },
                    n * ONE_HOUR
                );
                setShowLobby(true);
            }

            // if before scheduling window (and prestart), show hero section
            else if (schedulingData.from - prestartInMs > now) {
                console.info(">> it is not yet time");
                // set timeout to show player when scheduling window opens (consider prestart)
                const timeToUpdate = schedulingData.from - prestartInMs - now;
                console.info("will update in " + timeToUpdate / 1000 + " secs");
                safeSetTimeout(
                    () => {
                        decideWhatToDo(ServerTimeHelper.getUpdatedTime());
                    },
                    timeToUpdate
                );
                setShowLobby(true);
            }

            // if inside scheduling window
            else if (
                schedulingData.from - prestartInMs < now // started already
                && (
                    !schedulingData.to // no end
                    || (schedulingData.to && schedulingData.to + postEndInMs > now)
                ) // not yet ended
            ) {
                console.info(">> the time is now");
                setShowLobby(false);
            }
        };

        // get server time, then decide what to do
        ServerTimeHelper
            .getServerTime(serviceUrl)
            .then((value) => {
                decideWhatToDo(value);
            })
            .catch((err) => {
                console.info(">> failed getting system time, using local", err);
                decideWhatToDo(Date.now());
            });
    }, [serviceUrl, scheduleEvent, schedulingData, entry.id]);

    const { statusLabel, actionsToPass } = getPropsToPass(entry, schedulingData, actions, isReadOnly);

    const sizedThumbnailUrl = useThumbnailSizing(thumbnailUrl);

    return (
        <CommonHeroLayout collapsedHero={props.collapse}>
            {showLobby && (
                <Lobby
                    thumbnailUrl={sizedThumbnailUrl}
                    statusLabel={statusLabel}
                    title={entry.name}
                    schedulingData={schedulingData}
                    actions={actionsToPass}
                    data={<MediaLobbyDataContainer {...props} />}
                />
            )}
            {!showLobby && <Player media={media} config={playerConfig} className="kms-ds-media-page-player" />}
        </CommonHeroLayout>
    );
};

export default ScheduledMediaHero;
